import { GAME_CONFIG, ROUND_SCORE_TARGETS, ROUND_TILE_TARGETS } from '../config/gameConfig';
import { RARITY_ORDER } from '../config/rarityConfig';
import { SHOP_KINDS } from '../config/shopKinds';
import {
  NEW_TILE_LETTER_CHANCES,
  SHOP_RARITY_CHANCES,
} from '../config/shopConfig';
import {
  applyModifierIdToTileModifiers,
  getEnabledBoardModifierChances,
  getEnabledGameModifierChances,
  getEnabledNewTileChances,
  getEnabledTileUpgradeChances,
  getShopOptionModifierMeta,
  getShopOptionModifierRarity,
  getTileModifierMeta,
  getTileModifierRarity,
  isShopKindEnabled,
  normalizeTileModifiers,
  resolveModifierGrantedEffects,
} from '../config/shopOptions';

export const SHOP_CHOICE_COUNT = 3;
const LETTER_POOL = Object.keys(NEW_TILE_LETTER_CHANCES);

const normalizeRarityKey = (rarity) => {
  const normalized = String(rarity || '').trim().toLowerCase();
  return RARITY_ORDER.includes(normalized) ? normalized : 'common';
};

export const normalizeShopRarityChances = (rarityChances) =>
  RARITY_ORDER.reduce((acc, rarityKey) => {
    const fallbackValue = Number(SHOP_RARITY_CHANCES[rarityKey]) || 0;
    const raw = rarityChances?.[rarityKey];
    const parsed = Number(raw);
    acc[rarityKey] = Math.max(0, raw != null && Number.isFinite(parsed) ? parsed : fallbackValue);
    return acc;
  }, {});

const _roundToNearest5 = (n) => Math.round(n / 5) * 5;
const _lateGameGrowthFactor = 1.45;

export const getRoundTarget = (roundIndex = 1) => {
  const n = Math.max(1, Math.round(Number(roundIndex) || 1));
  if (n <= ROUND_SCORE_TARGETS.length) {
    return ROUND_SCORE_TARGETS[n - 1];
  }
  const lastTarget = ROUND_SCORE_TARGETS[ROUND_SCORE_TARGETS.length - 1];
  return _roundToNearest5(lastTarget * Math.pow(_lateGameGrowthFactor, n - ROUND_SCORE_TARGETS.length));
};

export const getRoundTileTarget = (roundIndex = 1) => {
  const n = Math.max(1, Math.round(Number(roundIndex) || 1));
  if (n <= ROUND_TILE_TARGETS.length) {
    return ROUND_TILE_TARGETS[n - 1];
  }
  return ROUND_TILE_TARGETS[ROUND_TILE_TARGETS.length - 1];
};

const { roundsPerAct } = GAME_CONFIG.actConfig;

export const getActForRound = (roundIndex = 1) =>
  Math.ceil(Math.max(1, roundIndex) / roundsPerAct);

export const getRoundInAct = (roundIndex = 1) =>
  ((Math.max(1, roundIndex) - 1) % roundsPerAct) + 1;

export const isActStart = (roundIndex = 1) =>
  ((Math.max(1, roundIndex) - 1) % roundsPerAct) === 0;

const pickWeighted = (entries, rng = Math.random) => {
  const normalized = (Array.isArray(entries) ? entries : [])
    .filter((entry) => Number(entry?.weight) > 0)
    .map((entry) => ({
      value: entry.value,
      weight: Number(entry.weight),
    }));

  if (!normalized.length) {
    return null;
  }

  const totalWeight = normalized.reduce((sum, entry) => sum + entry.weight, 0);
  const roll = rng() * totalWeight;

  let running = 0;
  for (const entry of normalized) {
    running += entry.weight;
    if (roll < running) {
      return entry.value;
    }
  }

  return normalized[normalized.length - 1].value;
};

const pickRandom = (items, rng = Math.random) => {
  if (!items.length) {
    return null;
  }

  const index = Math.floor(rng() * items.length);
  return items[index] || null;
};

const pickWeightedFromMap = (weightMap, allowedValues, rng = Math.random) => {
  const candidates = (Array.isArray(allowedValues) ? allowedValues : Object.keys(weightMap || {})).filter(
    (value) => Number(weightMap?.[value]) > 0
  );

  return pickWeighted(
    candidates.map((value) => ({
      value,
      weight: Number(weightMap[value]),
    })),
    rng
  );
};

const getModifierIdsForRarity = (weightMap, getRarity, targetRarity) =>
  Object.keys(weightMap || {}).filter(
    (modifierId) =>
      Number(weightMap?.[modifierId]) > 0 &&
      normalizeRarityKey(getRarity(modifierId)) === normalizeRarityKey(targetRarity)
  );

const createTileUpgradeOption = (context) => {
  const { bagTiles = [], rng = Math.random, usedSourceTileIds, targetRarity } = context;
  const availableTiles = (Array.isArray(bagTiles) ? bagTiles : []).filter(
    (tile) => tile?.id && !usedSourceTileIds.has(tile.id)
  );

  if (!availableTiles.length) {
    return null;
  }

  const nextTile = pickRandom(availableTiles, rng);
  if (!nextTile) {
    return null;
  }

  const currentModifiers = normalizeTileModifiers(nextTile.modifiers);
  const enabledUpgradeChances = getEnabledTileUpgradeChances();
  const weightedModifierIds = getModifierIdsForRarity(
    enabledUpgradeChances,
    getTileModifierRarity,
    targetRarity
  );

  if (!weightedModifierIds.length) {
    return null;
  }

  const availableModifierIds = weightedModifierIds.filter(
    (modifierId) => !currentModifiers.extras.includes(modifierId)
  );
  const selectedModifierId = pickWeightedFromMap(
    enabledUpgradeChances,
    availableModifierIds.length ? availableModifierIds : weightedModifierIds,
    rng
  );

  if (!selectedModifierId) {
    return null;
  }

  const modifierMeta = getTileModifierMeta(selectedModifierId);
  const letter = String(nextTile.letter || '').toUpperCase();
  const newModifiers = applyModifierIdToTileModifiers(currentModifiers, selectedModifierId);
  const isMultiplierUpgrade = newModifiers.multiplier !== currentModifiers.multiplier;

  return {
    kind: SHOP_KINDS.BAG_TILE_UPGRADE,
    rarity: normalizeRarityKey(getTileModifierRarity(selectedModifierId)),
    sourceTileId: nextTile.id,
    reservedSourceTileId: nextTile.id,
    tileLetter: letter,
    tileModifiers: currentModifiers,
    modifierId: selectedModifierId,
    modifierLabel: modifierMeta.label || selectedModifierId,
    modifierDescription: modifierMeta.description,
    title: 'Tile Upgrade',
    description: isMultiplierUpgrade && currentModifiers.multiplier > 1
      ? `Tile ${letter}: ${currentModifiers.multiplier}x → ${newModifiers.multiplier}x word score`
      : `Tile ${letter} gains ${modifierMeta.label || selectedModifierId}.`,
  };
};

const createNewRandomTileOption = (context) => {
  const { rng = Math.random, targetRarity } = context;
  const letter = pickWeightedFromMap(NEW_TILE_LETTER_CHANCES, LETTER_POOL, rng) || 'A';
  const enabledNewTileChances = getEnabledNewTileChances();
  const modifierIds = getModifierIdsForRarity(
    enabledNewTileChances,
    getTileModifierRarity,
    targetRarity
  );

  if (!modifierIds.length) {
    return null;
  }

  const modifierId = pickWeightedFromMap(enabledNewTileChances, modifierIds, rng) || modifierIds[0];
  const modifierMeta = modifierId === 'NORMAL' ? null : getTileModifierMeta(modifierId);
  const tileModifiers = applyModifierIdToTileModifiers(normalizeTileModifiers(), modifierId);

  return {
    kind: SHOP_KINDS.NEW_RANDOM_TILE,
    rarity: normalizeRarityKey(getTileModifierRarity(modifierId)),
    tileLetter: String(letter || 'A').toUpperCase(),
    tileModifiers,
    modifierLabel: modifierMeta?.label || 'None',
    modifierDescription: modifierMeta?.description || '',
    title: 'New Tile',
    description:
      modifierId === 'NORMAL'
        ? ''
        : `Starts with ${modifierMeta.label}.`,
  };
};

const createRemoveBagTileOption = (context) => {
  const { bagTiles = [], rng = Math.random, removedSourceTileIds, targetRarity } = context;
  if (normalizeRarityKey(targetRarity) !== 'uncommon') {
    return null;
  }

  const removableTiles = (Array.isArray(bagTiles) ? bagTiles : []).filter(
    (tile) => tile?.id && !removedSourceTileIds.has(tile.id)
  );

  if (!removableTiles.length) {
    return null;
  }

  const tileToRemove = pickRandom(removableTiles, rng);
  if (!tileToRemove) {
    return null;
  }

  return {
    kind: SHOP_KINDS.REMOVE_BAG_TILE,
    rarity: 'common',
    sourceTileId: tileToRemove.id,
    reservedSourceTileId: tileToRemove.id,
    tileLetter: String(tileToRemove.letter || '').toUpperCase(),
    tileModifiers: normalizeTileModifiers(tileToRemove.modifiers),
    modifierLabel: 'Remove',
    modifierDescription: 'Permanently remove this tile from your bag.',
    title: 'Remove Tile',
    description: `Remove ${String(tileToRemove.letter || '').toUpperCase()} from your bag.`,
  };
};

const createGameModifierOption = (context) => {
  const { rng = Math.random, runSettings, targetRarity } = context;
  const enabledGameChances = getEnabledGameModifierChances();
  const modifierIds = getModifierIdsForRarity(
    enabledGameChances,
    getShopOptionModifierRarity,
    targetRarity
  );
  const modifierId = pickWeightedFromMap(enabledGameChances, modifierIds, rng);
  if (!modifierId) {
    return null;
  }

  const modifierMeta = getShopOptionModifierMeta(modifierId);
  const grantedEffects = resolveModifierGrantedEffects(modifierId, {
    runSettings,
    rng,
  });
  const defaultLabel = modifierMeta.label || modifierId;
  const resolvedLabel =
    typeof modifierMeta.shopLabelResolver === 'function'
      ? modifierMeta.shopLabelResolver({
          grantedEffects,
          modifierId,
          defaultLabel,
        })
      : defaultLabel;
  const resolvedDescription = modifierMeta.description || '';

  return {
    kind: SHOP_KINDS.GAME_MODIFIER_GRANT,
    rarity: normalizeRarityKey(getShopOptionModifierRarity(modifierId)),
    modifierId,
    modifierLabel: resolvedLabel,
    modifierDescription: resolvedDescription,
    grantedEffects,
    title: 'Game Modifier',
    description: resolvedDescription,
  };
};

const createBoardModifierOption = (context) => {
  const { rng = Math.random, targetRarity } = context;
  const enabledBoardChances = getEnabledBoardModifierChances();
  const modifierIds = getModifierIdsForRarity(
    enabledBoardChances,
    getShopOptionModifierRarity,
    targetRarity
  );
  const modifierId = pickWeightedFromMap(enabledBoardChances, modifierIds, rng);
  if (!modifierId) {
    return null;
  }

  const modifierMeta = getShopOptionModifierMeta(modifierId);

  return {
    kind: SHOP_KINDS.BOARD_MODIFIER_GRANT,
    rarity: normalizeRarityKey(getShopOptionModifierRarity(modifierId)),
    modifierId,
    boardSquareModifierId: modifierMeta.boardSquareModifierId || modifierId,
    modifierLabel: modifierMeta.label || modifierId,
    modifierDescription: modifierMeta.description || '',
    title: 'Board Modifier',
    description: modifierMeta.description || '',
  };
};

const SHOP_BADGE_GENERATORS = {
  [SHOP_KINDS.BAG_TILE_UPGRADE]: {
    isAvailable: (context) =>
      (Array.isArray(context?.bagTiles) ? context.bagTiles : []).some(
        (tile) => tile?.id && !context.usedSourceTileIds.has(tile.id)
      ),
    canCreateForRarity: (targetRarity) =>
      getModifierIdsForRarity(getEnabledTileUpgradeChances(), getTileModifierRarity, targetRarity).length > 0,
    createOption: createTileUpgradeOption,
  },
  [SHOP_KINDS.NEW_RANDOM_TILE]: {
    isAvailable: () => LETTER_POOL.length > 0,
    canCreateForRarity: (targetRarity) =>
      ['rare', 'epic', 'legendary'].includes(normalizeRarityKey(targetRarity)) &&
      getModifierIdsForRarity(getEnabledNewTileChances(), getTileModifierRarity, targetRarity).length > 0,
    createOption: createNewRandomTileOption,
  },
  [SHOP_KINDS.REMOVE_BAG_TILE]: {
    isAvailable: (context) =>
      isShopKindEnabled('REMOVE_BAG_TILE') &&
      (Array.isArray(context?.bagTiles) ? context.bagTiles : []).some(
        (tile) => tile?.id && !context.removedSourceTileIds.has(tile.id)
      ),
    canCreateForRarity: (targetRarity) => normalizeRarityKey(targetRarity) === 'uncommon',
    createOption: createRemoveBagTileOption,
  },
  [SHOP_KINDS.GAME_MODIFIER_GRANT]: {
    isAvailable: () => Object.keys(getEnabledGameModifierChances()).length > 0,
    canCreateForRarity: (targetRarity) =>
      getModifierIdsForRarity(getEnabledGameModifierChances(), getShopOptionModifierRarity, targetRarity).length > 0,
    createOption: createGameModifierOption,
  },
  [SHOP_KINDS.BOARD_MODIFIER_GRANT]: {
    isAvailable: () => Object.keys(getEnabledBoardModifierChances()).length > 0,
    canCreateForRarity: (targetRarity) =>
      getModifierIdsForRarity(getEnabledBoardModifierChances(), getShopOptionModifierRarity, targetRarity).length > 0,
    createOption: createBoardModifierOption,
  },
};

export const drawShopOptions = ({
  roundIndex = 1,
  bagTiles = [],
  count = SHOP_CHOICE_COUNT,
  rarityChances,
  runSettings,
  rng = Math.random,
} = {}) => {
  const maxCount = Math.max(1, Number(count) || SHOP_CHOICE_COUNT);
  const normalizedRarityChances = normalizeShopRarityChances(rarityChances);
  const selected = [];
  const context = {
    roundIndex,
    bagTiles: (Array.isArray(bagTiles) ? bagTiles : []).filter(Boolean),
    runSettings,
    rng,
    usedSourceTileIds: new Set(),
    removedSourceTileIds: new Set(),
  };
  const maxAttempts = maxCount * 40;
  let attempts = 0;

  while (selected.length < maxCount && attempts < maxAttempts) {
    attempts += 1;

    const targetRarity =
      pickWeightedFromMap(normalizedRarityChances, RARITY_ORDER, rng) || RARITY_ORDER[0];

    const availableBadges = Object.entries(SHOP_BADGE_GENERATORS)
      .filter(([, badge]) => badge.isAvailable(context) && badge.canCreateForRarity(targetRarity))
      .map(([badgeKind]) => badgeKind);

    const nextBadgeKind = pickRandom(availableBadges, rng);
    if (!nextBadgeKind) {
      continue;
    }

    const option = SHOP_BADGE_GENERATORS[nextBadgeKind].createOption({
      ...context,
      targetRarity,
    });
    if (!option) {
      continue;
    }

    if (option.kind === SHOP_KINDS.BAG_TILE_UPGRADE && option.reservedSourceTileId) {
      context.usedSourceTileIds.add(option.reservedSourceTileId);
    }
    if (option.kind === SHOP_KINDS.REMOVE_BAG_TILE && option.reservedSourceTileId) {
      context.removedSourceTileIds.add(option.reservedSourceTileId);
    }

    selected.push(option);
  }

  return selected.map((option, index) => {
    const { reservedSourceTileId, ...rest } = option;

    return {
      ...rest,
      rarity: normalizeRarityKey(option.rarity),
      id: `shop-r${roundIndex}-c${index + 1}`,
    };
  });
};

const createNewBagTileId = (bagTiles = [], letter = 'A') => {
  const normalizedLetter = String(letter || 'A').toUpperCase();
  const prefix = `bag-${normalizedLetter}-new-`;
  const existingIds = new Set((Array.isArray(bagTiles) ? bagTiles : []).map((tile) => tile?.id));

  let nextIndex = 1;
  while (existingIds.has(`${prefix}${nextIndex}`)) {
    nextIndex += 1;
  }

  return `${prefix}${nextIndex}`;
};

export const applyBagTileUpgrade = (bagTiles = [], option) =>
  (Array.isArray(bagTiles) ? bagTiles : []).map((tile) => {
    if (!tile || tile.id !== option?.sourceTileId) {
      return tile;
    }

    return {
      ...tile,
      modifiers: applyModifierIdToTileModifiers(tile.modifiers, option.modifierId),
    };
  });

const applyNewRandomTile = (bagTiles = [], option) => {
  const normalizedLetter = String(option?.tileLetter || '').toUpperCase();

  if (!normalizedLetter) {
    return Array.isArray(bagTiles) ? bagTiles : [];
  }

  const newTile = {
    id: createNewBagTileId(bagTiles, normalizedLetter),
    letter: normalizedLetter,
    modifiers: normalizeTileModifiers(option?.tileModifiers),
  };

  return [...(Array.isArray(bagTiles) ? bagTiles : []), newTile];
};

const applyRemoveBagTile = (bagTiles = [], option) =>
  (Array.isArray(bagTiles) ? bagTiles : []).filter((tile) => tile?.id !== option?.sourceTileId);

const SHOP_BADGE_APPLIERS = {
  [SHOP_KINDS.BAG_TILE_UPGRADE]: applyBagTileUpgrade,
  [SHOP_KINDS.NEW_RANDOM_TILE]: applyNewRandomTile,
  [SHOP_KINDS.REMOVE_BAG_TILE]: applyRemoveBagTile,
};

export const applyShopOption = (bagTiles = [], option) => {
  const apply = SHOP_BADGE_APPLIERS[option?.kind];
  if (!apply) {
    return Array.isArray(bagTiles) ? bagTiles : [];
  }

  return apply(bagTiles, option);
};

export const resolveShopOptionGrantedEffects = (option, context = {}) => {
  if (option?.kind !== SHOP_KINDS.GAME_MODIFIER_GRANT || !option?.modifierId) {
    return [];
  }

  if (Array.isArray(option?.grantedEffects) && option.grantedEffects.length) {
    return option.grantedEffects;
  }

  return resolveModifierGrantedEffects(option.modifierId, context);
};